import React, { useEffect, useMemo, useState } from 'react';

// A lightweight React renderer for FormDef-compatible payloads.
// Supports common field types and mirrors the existing PHP/JS form schema.
const normalizeFields = (form) => {
    if (!form) return [];
    if (Array.isArray(form.properties)) return form.properties;
    if (Array.isArray(form.fields)) return form.fields;
    if (Array.isArray(form)) return form;
    // Some exports store fields as object map of index -> field
    if (form.properties && typeof form.properties === 'object') {
        return Object.values(form.properties);
    }
    if (form.fields && typeof form.fields === 'object') {
        return Object.values(form.fields);
    }
    return [];
};

const normalizeOptions = (values) => {
    if (!values) return [];
    if (Array.isArray(values)) return values;
    if (typeof values === 'object') {
        return Object.keys(values).map((key) => ({ label: values[key], value: key }));
    }
    return [];
};

const evaluateSingleCondition = (condition, values) => {
    if (!condition || !condition.field) return true;
    const operator = (condition.operator || 'equal').toLowerCase();
    const left = values[condition.field];
    const right = condition.value;

    switch (operator) {
    case 'equal':
    case 'equ':
    case '==':
        return left == right; // eslint-disable-line eqeqeq
    case 'not_equal':
    case 'not_equ':
    case '!=':
        return left != right; // eslint-disable-line eqeqeq
    case 'contains':
        if (Array.isArray(left)) return left.includes(right);
        if (typeof left === 'string') return left.indexOf(right) !== -1;
        return false;
    case 'in':
        if (Array.isArray(right)) return right.includes(left);
        return false;
    case '>':
        return left > right;
    case '<':
        return left < right;
    case '>=':
        return left >= right;
    case '<=':
        return left <= right;
    default:
        return true;
    }
};

const evaluateConditions = (conditions, values) => {
    if (!Array.isArray(conditions) || conditions.length === 0) return true;

    // Support grouped OR blocks if provided as { operator: 'or', conditions: [...] }
    return conditions.every((cond) => {
        if (cond && cond.operator && cond.operator.toLowerCase() === 'or' && Array.isArray(cond.conditions)) {
            return cond.conditions.some((nested) => evaluateSingleCondition(nested, values));
        }
        return evaluateSingleCondition(cond, values);
    });
};

const FormRenderer = ({
    form,
    initialValues = {},
    readOnly = false,
    onChange,
    onSubmit,
    submitLabel = 'Save',
    showSubmit = false,
}) => {
    const fields = useMemo(() => {
        const normalized = normalizeFields(form);
        console.debug('[FormRenderer] normalize fields', { input: form, normalized });
        return normalized;
    }, [form]);
    const [values, setValues] = useState(initialValues || {});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        console.debug('[FormRenderer] initialValues change', initialValues);
        setValues(initialValues || {});
    }, [initialValues]);

    const updateValue = (name, value) => {
        setValues((prev) => {
            const next = { ...prev, [name]: value };
            console.debug('[FormRenderer] updateValue', { name, value, prev, next });
            if (onChange) {
                onChange(next);
            }
            return next;
        });
    };

    const isFieldVisible = (field) => {
        if (!field) return false;
        if (!field.conditions) return true;
        return evaluateConditions(field.conditions, values);
    };

    const validate = () => {
        const nextErrors = {};
        fields.forEach((field) => {
            if (!isFieldVisible(field)) return;
            const required = field.required || field.is_required;
            if (!required) return;
            const val = values[field.name];
            if (val === undefined || val === null || val === '') {
                nextErrors[field.name] = 'Required';
            }
        });
        console.debug('[FormRenderer] validate', { values, nextErrors });
        setErrors(nextErrors);
        return nextErrors;
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        console.debug('[FormRenderer] handleSubmit start', { values });
        const errs = validate();
        if (Object.keys(errs).length === 0 && onSubmit) {
            onSubmit(values);
        }
    };

    const renderInput = (field) => {
        const { type = 'text', subtype, name, label, help, instructions } = field;
        const current = values[name];
        const common = {
            name,
            id: name,
            className: 'form-control',
            disabled: readOnly,
            value: current ?? '',
            onChange: (e) => updateValue(name, e.target.value),
        };

        if (type === 'hidden') {
            return <input type="hidden" {...common} />;
        }

        if (type === 'textarea') {
            return <textarea {...common} rows={field.rows || 4} />;
        }

        if (type === 'select') {
            const options = normalizeOptions(field.values);
            const selectProps = {
                ...common,
                multiple: field.multiple || false,
                value: field.multiple ? current || [] : current || '',
                onChange: (e) => {
                    if (field.multiple) {
                        const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                        updateValue(name, selected);
                    } else {
                        updateValue(name, e.target.value);
                    }
                },
            };
            return (
                <select {...selectProps}>
                    {!field.multiple && <option value="">Select…</option>}
                    {options.map((opt) => (
                        <option key={opt.value || opt.label} value={opt.value || opt.label}>
                            {opt.label || opt.value}
                        </option>
                    ))}
                </select>
            );
        }

        if (type === 'checkbox-group') {
            const options = normalizeOptions(field.values);
            const selected = Array.isArray(current) ? current : [];
            return (
                <div>
                    {options.map((opt) => {
                        const checked = selected.includes(opt.value);
                        return (
                            <label key={opt.value || opt.label} className="checkbox-inline" style={{ marginRight: 10 }}>
                                <input
                                    type="checkbox"
                                    disabled={readOnly}
                                    checked={checked}
                                    onChange={(e) => {
                                        const next = e.target.checked
                                            ? [...selected, opt.value]
                                            : selected.filter((v) => v !== opt.value);
                                        updateValue(name, next);
                                    }}
                                />{' '}
                                {opt.label || opt.value}
                            </label>
                        );
                    })}
                </div>
            );
        }

        if (type === 'radio-group') {
            const options = normalizeOptions(field.values);
            return (
                <div>
                    {options.map((opt) => {
                        const checked = current === opt.value;
                        return (
                            <label key={opt.value || opt.label} className="radio-inline" style={{ marginRight: 10 }}>
                                <input
                                    type="radio"
                                    name={name}
                                    disabled={readOnly}
                                    checked={checked}
                                    onChange={() => updateValue(name, opt.value)}
                                />{' '}
                                {opt.label || opt.value}
                            </label>
                        );
                    })}
                </div>
            );
        }

        if (type === 'header') {
            const Tag = subtype || 'h4';
            return <Tag style={{ marginTop: 0 }}>{label || field.value || ''}</Tag>;
        }

        if (type === 'paragraph') {
            const Tag = subtype || 'p';
            return <Tag>{field.value || label || ''}</Tag>;
        }

        if (type === 'button') {
            return (
                <button
                    type={subtype || 'button'}
                    className={`btn btn-${field.style || 'default'}`}
                    disabled={readOnly}
                    onClick={(e) => {
                        if (field.action === 'submit') {
                            handleSubmit(e);
                        }
                    }}
                >
                    {label || field.value || 'Button'}
                </button>
            );
        }

        // Default input (text/number/date/etc.)
        return <input type={subtype || type || 'text'} {...common} />;
    };

    return (
        <form onSubmit={handleSubmit}>
            {fields.map((field) => {
                const required = field.required || field.is_required;
                const err = errors[field.name];
                const isHidden = field.type === 'hidden';
                const fieldInstructions = field.instructions;
                const visible = isFieldVisible(field);
                if (!visible) return null;
                return (
                    <div key={field.name} className={`form-group${isHidden ? ' hidden' : ''}`}>
                        {!isHidden && (
                            <label className="control-label">
                                {field.label || field.name}
                                {required && <span className="text-danger"> *</span>}
                            </label>
                        )}
                        {renderInput(field)}
                        {field.help && !readOnly && (
                            <p className="help-block">{field.help}</p>
                        )}
                        {fieldInstructions && !readOnly && (
                            <p className="help-block">{fieldInstructions}</p>
                        )}
                        {err && <p className="text-danger" style={{ marginTop: 5 }}>{err}</p>}
                    </div>
                );
            })}
            {showSubmit && (
                <div style={{ marginTop: '10px' }}>
                    <button type="submit" className="btn btn-primary" disabled={readOnly}>
                        {submitLabel}
                    </button>
                </div>
            )}
        </form>
    );
};

export default FormRenderer;
